import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { JobState } from '@treadinc/horizon-api-spec';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { ComplianceWarningDialog } from '~components/CompliancePanel/ComplianceWarningDialog';
import { JobAssignmentType } from '~constants/enums';
import { Account } from '~hooks/useAccount/models';
import useComplianceCheck, {
  ComplianceCheckResult,
} from '~hooks/useComplianceCheck/useComplianceCheck';
import { Job } from '~hooks/useJob/models';
import { User } from '~hooks/useUsers/models';
import { useStores } from '~store';

import { AssignDriverMenu } from './AssignDriverMenu';
import { AssignedDriverView } from './AssignedDriverView';
import { SelectedAssignment } from './hooks/useAssignDriverMenu';
import { useJobAssignment } from './hooks/useJobAssignment';

interface AssignedJobToViewProps {
  companyId: string;
  customAssignCTA?: ReactElement;
  customUnassignCTA?: ReactElement;
  driver?: User;
  driverOrVendorAssignable: boolean;
  editable?: boolean;
  filterDate?: string;
  isUnassignable?: boolean;
  jobId: string;
  onError?: () => void;
  onLoadingStatusChange?: (isLoading: boolean) => void;
  onSuccess?: (job: Job) => void;
  status: JobState;
  sx?: SxProps<Theme>; // Style
  valueToDisplay?: 'driver' | 'vendor';
  vendor?: Account;
}

type ShouldShowReadRemoveOnlyViewFnArgs = Pick<
  AssignedJobToViewProps,
  'driver' | 'driverOrVendorAssignable' | 'status' | 'valueToDisplay' | 'vendor'
>;
export const shouldShowReadRemoveOnlyView = ({
  driver,
  driverOrVendorAssignable,
  vendor,
  status,
  valueToDisplay,
}: ShouldShowReadRemoveOnlyViewFnArgs) => {
  const isJobAssigned = !!vendor || !!driver;

  // The backend only permits assignment while a job is still awaiting one: its
  // AASM `assign` event transitions from `created`/`accepted` only. Once a job
  // has moved past that — e.g. already `sent` to a vendor — re-firing `assign`
  // is a 409 conflict, so we must stop offering the Assign action even when the
  // local vendor/driver prop is momentarily stale. This also covers the former
  // `canceled`/`ended` cases by construction (TRE-15790).
  const jobIsAwaitingAssignment =
    status === JobState.CREATED || status === JobState.ACCEPTED;

  // Seeing a job does not entitle you to dispatch it: the backend gates that on the
  // company holding the job, so withhold the control rather than have it be refused.
  return (
    !driverOrVendorAssignable ||
    isJobAssigned ||
    valueToDisplay === 'driver' ||
    !jobIsAwaitingAssignment
  );
};

const AssignJobToView = ({
  companyId,
  customAssignCTA,
  customUnassignCTA,
  driver,
  driverOrVendorAssignable,
  editable = false,
  filterDate,
  isUnassignable,
  jobId,
  onError,
  onLoadingStatusChange,
  onSuccess,
  status,
  valueToDisplay,
  vendor,
}: AssignedJobToViewProps) => {
  const { userStore } = useStores();
  const [_, setShowAssignPopover] = useState<boolean>(false);
  const { handleAssign, handleUnassign, isLoading } = useJobAssignment(
    jobId,
    driver,
    onSuccess,
    onError,
  );

  const {
    isLoading: isCheckingCompliance,
    result: complianceResult,
    check: checkCompliance,
    reset: resetCompliance,
  } = useComplianceCheck();
  const [pendingAssignment, setPendingAssignment] = useState<SelectedAssignment | null>(
    null,
  );
  const [showComplianceWarning, setShowComplianceWarning] = useState(false);
  const assignInFlightRef = useRef(false);

  const handleAssignWithComplianceCheck = useCallback(
    async (selectedAssignment: SelectedAssignment) => {
      // Guard against a double-submit. `isLoading` from useJobAssignment can't do
      // this: it's React state set inside handleAssign, and only after the awaited
      // compliance check — so two rapid selections both read it as false and each
      // fire an `assign`, which 409s once the first moves the job past its
      // assignable state. A ref flips synchronously at entry, before any await, so
      // the concurrent second submit is dropped (TRE-15790).
      if (assignInFlightRef.current) {
        return;
      }
      assignInFlightRef.current = true;

      try {
        const isVendor = selectedAssignment.type === JobAssignmentType.VENDOR;
        const isDriver = selectedAssignment.type === JobAssignmentType.DRIVER;

        if (isVendor || isDriver) {
          const checkResult: ComplianceCheckResult = await checkCompliance(
            companyId,
            isVendor ? selectedAssignment.id : undefined,
            isDriver ? selectedAssignment.id : undefined,
          );

          if (checkResult.hasIssues) {
            setPendingAssignment(selectedAssignment);
            setShowComplianceWarning(true);
            return;
          }
        }

        await handleAssign(selectedAssignment);
      } finally {
        assignInFlightRef.current = false;
      }
    },
    [companyId, checkCompliance, handleAssign],
  );

  const handleComplianceOverride = useCallback(async () => {
    if (pendingAssignment) {
      try {
        await handleAssign(pendingAssignment, true);
      } finally {
        setShowComplianceWarning(false);
        setPendingAssignment(null);
        resetCompliance();
      }
    }
  }, [pendingAssignment, handleAssign, resetCompliance]);

  const handleComplianceCancel = useCallback(() => {
    setShowComplianceWarning(false);
    setPendingAssignment(null);
    resetCompliance();
  }, [resetCompliance]);

  const hasReadRemoveOnlyView = shouldShowReadRemoveOnlyView({
    driver,
    driverOrVendorAssignable,
    vendor,
    status,
    valueToDisplay,
  });
  const isEditable = editable && userStore.getPermissions().canAssignJob;

  useEffect(() => {
    onLoadingStatusChange?.(isLoading || isCheckingCompliance);
  }, [onLoadingStatusChange, isLoading, isCheckingCompliance]);

  return (
    <>
      {!hasReadRemoveOnlyView && (
        <AssignDriverMenu
          companyId={companyId}
          customAssignCTA={customAssignCTA}
          filterDate={filterDate}
          onOpenStateChanged={(open) => {
            setShowAssignPopover(open);
            if (!open) {
              setShowAssignPopover(false);
            }
          }}
          onChange={handleAssignWithComplianceCheck}
        />
      )}

      <AssignedDriverView
        customUnassignCTA={customUnassignCTA}
        driver={driver}
        editable={isEditable}
        isUnassignable={isUnassignable}
        onUnassignClick={handleUnassign}
        valueToDisplay={valueToDisplay}
        vendor={vendor}
      />

      {complianceResult && (
        <ComplianceWarningDialog
          isOpen={showComplianceWarning}
          result={complianceResult}
          isSubmitting={isLoading}
          onCancel={handleComplianceCancel}
          onOverride={handleComplianceOverride}
        />
      )}
    </>
  );
};

export { AssignJobToView };
